import {
	ContextService,
	DriveService,
	EventService,
	FootballService,
	GamedayApiService,
	GameService,
	SeasonService,
	SportResolverService,
	SyncServiceFactory,
	TennisService
} from "@prestosports/prestostats-core/Services";
import { EventStatus } from "@prestosports/prestostats-core/Common";
import {
	CONFIRM_PERIOD_NOTIFICATION,
	CONFIRM_PERIOD_NOTIFICATION_COMPLETE,
	CREATE_STATS_COMPLETE,
	DELETE_STATS_COMPLETE,
	FINALIZE_GAME_COMPLETE,
	GET_PROMPT_RECEIVING_TEAM_COMPLETE,
	KEEP_SWAPPING_TEAMS,
	KEEP_SWAPPING_TEAMS_COMPLETE,
	ORDER_PLAYERS_BY_POSITION,
	ORDER_PLAYERS_BY_POSITION_COMPLETE,
	SAVE_FINALIZATION_SETTINGS_COMPLETE,
	SAVE_MATCHES_FINALIZATION_ORDER,
	SAVE_MATCHES_FINALIZATION_ORDER_COMPLETE,
	SET_OVERTIME_NOTIFICATION,
	SET_OVERTIME_NOTIFICATION_COMPLETE,
	SET_TIME_AND_PERIOD_COMPLETE,
	SET_TIME_AND_PERIOD_ERROR,
	SHOW_TIME_CONFIG,
	SWAP_TEAMS,
	SWAP_TEAMS_COMPLETE,
	UPDATE_RECEIVING_TEAM_COMPLETE,
	UPDATE_STATS_COMPLETE,
	VALIDATE_GAME_DATA,
	VALIDATE_GAME_DATA_COMPLETE
} from "./constants";

import { refreshPlays, startOver, startPlay } from "../football-play/actions";
import GamedayApiClient from "../../services/gameday-api-client";
import {
	getCompletedAndUpcomingEvents,
	updateCurrentEvent
} from "../events/actions";
import { GameRoutes } from "../../routes/game-routes";
import { getGameAccess, withQueuedSync, withSubmitting } from "../app/actions";
import Alert from "../../util/alert";

GamedayApiService.setGamedayApiClient(GamedayApiClient);

export const updateGameContext = (gameContext, dispatch) => {
	dispatch({
		type: UPDATE_STATS_COMPLETE,
		payload: gameContext
	});
};

export const createGameContext = (gameContext, dispatch) => {
	dispatch({
		type: CREATE_STATS_COMPLETE,
		payload: gameContext
	});
};

export const deleteGameContext = async (gameContext, dispatch) => {
	await ContextService.deleteContext(gameContext.gameId);
	dispatch({
		type: DELETE_STATS_COMPLETE
	});
};

export const finalizeGame = async (
	seasonId,
	gameId,
	values,
	redirect,
	dispatch
) =>
	withSubmitting(async () => {
		const game = await GameService.getGame(gameId);
		let gameContext;
		let event;
		const service = await SportResolverService.resolveSportService(
			game.sportCode
		);
		if (service) {
			({ gameContext, event } = await service.finalizeGame(gameId, values));
		}

		await updateCurrentEvent(event, dispatch);
		await getCompletedAndUpcomingEvents(seasonId, {}, {}, {}, dispatch);
		dispatch({
			type: FINALIZE_GAME_COMPLETE,
			payload: gameContext
		});
		getGameAccess(game.id, dispatch);
		redirect(`${GameRoutes.STATS.url.replace(":eventId", game.eventId)}`);
	}, dispatch);

export const saveFinalizationSettings = async (
	gameId,
	values,
	redirect,
	dispatch
) => {
	const game = await GameService.getGame(gameId);
	let event = await EventService.getById(game.eventId);

	const handleSaveSettings = async () => {
		let gameContext;
		const service = await SportResolverService.resolveSportService(
			game.sportCode
		);
		if (service) {
			({ gameContext, event } = await service.saveFinalizationSettings(
				gameId,
				values
			));
		}
		await updateCurrentEvent(event, dispatch);
		dispatch({
			type: SAVE_FINALIZATION_SETTINGS_COMPLETE,
			payload: gameContext
		});

		Alert.success("Event saved");
	};

	if (EventStatus.isStarted(event)) {
		withQueuedSync(event.id, handleSaveSettings, dispatch);
	} else {
		handleSaveSettings();
	}
};

const isPeriodChange = (period, gameContext) => {
	return !gameContext.periods.find((p) => p === period);
};

export const setTimeAndPeriod = async (
	seconds,
	period,
	gameContext,
	dispatch
) =>
	withSubmitting(async () => {
		const { gameId } = gameContext;
		const { eventId } = await GameService.getGame(gameId);
		return withQueuedSync(
			eventId,
			async () => {
				try {
					const { play } = await FootballService.getOrCreatePlay(gameId);
					await startOver(play, dispatch);

					const { gameContext: saved } = await FootballService.setTimeAndPeriod(
						gameContext.gameId,
						seconds,
						period
					);

					refreshPlays(await FootballService.getGamePlays(gameId), dispatch);

					dispatch({
						type: SET_TIME_AND_PERIOD_COMPLETE,
						payload: saved
					});

					if (isPeriodChange(period, gameContext)) {
						await startPlay(gameContext.gameId, dispatch);
					}
				} catch (error) {
					dispatch({
						type: SET_TIME_AND_PERIOD_ERROR,
						payload: { validationErrors: [error] }
					});
				}
			},
			dispatch
		);
	}, dispatch);

export const showTimeConfig = (value, dispatch) => {
	dispatch({
		type: SHOW_TIME_CONFIG,
		payload: { showTimeConfig: value }
	});
};

export const validateGameData = async (gameId, dispatch) => {
	dispatch({
		type: VALIDATE_GAME_DATA
	});

	const game = await GameService.getGame(gameId);
	const service = await SportResolverService.resolveSportService(game.sportCode);
	const { missingGameData } = await service.isGameDataComplete(gameId);

	dispatch({
		type: VALIDATE_GAME_DATA_COMPLETE,
		payload: { missingGameData }
	});
};

export const setOvertimeNotification = async (gameId, value, dispatch) => {
	dispatch({
		type: SET_OVERTIME_NOTIFICATION
	});

	const saved = await FootballService.setOvertimeNotification(gameId, value);
	dispatch({
		type: SET_OVERTIME_NOTIFICATION_COMPLETE,
		payload: saved
	});
};

export const swapTeams = async (gameId, swap, dispatch) => {
	dispatch({
		type: SWAP_TEAMS
	});

	let saved = await FootballService.swapTeams(gameId, swap);
	saved = await FootballService.confirmPeriodNotification(gameId);
	dispatch({
		type: SWAP_TEAMS_COMPLETE,
		payload: saved
	});
};

export const orderPlayersByPosition = async (gameId, order, dispatch) => {
	dispatch({
		type: ORDER_PLAYERS_BY_POSITION
	});

	const saved = await FootballService.orderPlayersByPosition(gameId, order);
	dispatch({
		type: ORDER_PLAYERS_BY_POSITION_COMPLETE,
		payload: saved
	});
};

export const keepSwappingTeams = async (gameId, swap, dispatch) => {
	dispatch({
		type: KEEP_SWAPPING_TEAMS
	});

	const saved = await FootballService.keepSwappingTeams(gameId, swap);
	dispatch({
		type: KEEP_SWAPPING_TEAMS_COMPLETE,
		payload: saved
	});
};

export const confirmPeriodNotification = async (gameId, dispatch) => {
	dispatch({
		type: CONFIRM_PERIOD_NOTIFICATION
	});

	const saved = await FootballService.confirmPeriodNotification(gameId);
	dispatch({
		type: CONFIRM_PERIOD_NOTIFICATION_COMPLETE,
		payload: saved
	});
};

export const saveMatchesFinalizationOrder = async (
	gameId,
	isSingles,
	orderedIds,
	dispatch
) => {
	const game = await GameService.getGame(gameId);
	withQueuedSync(
		game.eventId,
		async () => {
			dispatch({
				type: SAVE_MATCHES_FINALIZATION_ORDER
			});
			if (gameId) {
				const gameContext = await TennisService.updateMatchFinalizationOrder(
					gameId,
					isSingles,
					orderedIds
				);

				dispatch({
					type: SAVE_MATCHES_FINALIZATION_ORDER_COMPLETE,
					payload: gameContext
				});
			}
		},
		dispatch
	);
};

export const handlePeriodTransition = async (gameId, dispatch) => {
	const promptReceivingTeam = await FootballService.getPromptOffensiveTeam(
		gameId
	);
	dispatch({
		type: GET_PROMPT_RECEIVING_TEAM_COMPLETE,
		payload: { promptReceivingTeam }
	});
	const gameContext = await ContextService.getGameContext(gameId);
	const { triggerNewPeriodNotification } = gameContext;
	if (
		triggerNewPeriodNotification &&
		(!promptReceivingTeam || gameContext.getOvertimePeriod())
	) {
		// triggerSwapTeams is computed per ruleset by the PeriodStart rule
		// (e.g. indoor football keeps sides across halftime)
		if (gameContext.triggerSwapTeams) {
			await swapTeams(gameId, !gameContext.swapTeams, dispatch);
		} else {
			await confirmPeriodNotification(gameId, dispatch);
		}
	}
};

export const updateOffensiveTeam = async (gameId, teamId, dispatch) => {
	const { context, play } = await FootballService.updateOffensiveTeam(
		gameId,
		teamId
	);

	dispatch({
		type: UPDATE_RECEIVING_TEAM_COMPLETE,
		payload: context
	});

	await startOver(play, dispatch);
};

export const rebuildDriveTimesForGame = async (gameId) => {
	if (!gameId) {
		return { ok: false, reason: "no-game" };
	}
	const result = await FootballService.rebuildDriveTimes(gameId);
	return {
		ok: true,
		reason: result ? "rebuilt" : "no-complete-plays"
	};
};

export const getBoxscoreJsonForGame = async (gameId) => {
	if (!gameId) {
		return null;
	}
	return FootballService.getBoxscoreJson(gameId);
};

export const pushBoxscoreForGame = async (gameId) => {
	let result = { ok: false, reason: "no-game" };
	if (gameId) {
		const boxscore = await FootballService.getBoxscoreJson(gameId);
		if (boxscore) {
			await GamedayApiClient.saveBoxscore(boxscore);
			result = { ok: true, reason: "pushed" };
		} else {
			result = { ok: false, reason: "no-boxscore" };
		}
	}
	return result;
};

export const getEventDataForGame = async (gameId) => {
	if (!gameId) {
		return null;
	}
	const game = await GameService.getGame(gameId);
	if (!game) {
		return null;
	}
	const syncService = SyncServiceFactory.getInstance(game.sportCode);
	if (!syncService) {
		return null;
	}
	return syncService.collectEventData(gameId);
};

export const rebuildDriveAssignmentsForGame = async (gameId) => {
	if (!gameId) {
		return { ok: false, fixedPlayIds: [] };
	}
	const { fixedPlayIds } = await DriveService.rebuildDriveAssignments(gameId);
	return { ok: true, fixedPlayIds };
};

/**
 * Pause the game clock via core. Persists to IndexedDB and updates Redux.
 * Pass byPlaySequence: true when pause is initiated by starting a play.
 */
export const pauseGameClock = async (
	gameId,
	dispatch,
	{ byPlaySequence = false } = {}
) => {
	if (!gameId) return;
	const updated = await ContextService.pauseGameClock(gameId, {
		byPlaySequence
	});
	if (updated) {
		updateGameContext(updated, dispatch);
	}
};

/**
 * Resume the game clock via core. Persists to IndexedDB and updates Redux.
 * Pass onlyIfByPlaySequence: true to skip when user had manually paused
 * (preserves manual pauses across play sequences).
 */
export const resumeGameClock = async (
	gameId,
	dispatch,
	{ onlyIfByPlaySequence = false } = {}
) => {
	if (!gameId) return;
	const updated = await ContextService.resumeGameClock(gameId, {
		onlyIfByPlaySequence
	});
	if (updated) {
		updateGameContext(updated, dispatch);
	}
};

export const listLocalGames = async () => {
	const games = await GameService.getAllGames();
	// Seasons repeat across games; resolve each one once.
	const seasonNames = new Map();
	const entries = await Promise.all(
		games.map(async (game) => {
			let label = `Game ${game.id}`;
			let externalEventId = null;
			let sportCode = game.sportCode || null;
			let seasonId = null;
			try {
				const event = await EventService.getById(game.eventId);
				const away = event?.teams?.awayTeam?.teamName || "Away";
				const home = event?.teams?.homeTeam?.teamName || "Home";
				const date = event?.date ? `${event.date} — ` : "";
				externalEventId = event?.eventId;
				const idTag = externalEventId ? ` (${externalEventId})` : "";
				label = `${date}${away} @ ${home}${idTag}`;
				sportCode = sportCode || event?.sportId || null;
				seasonId = event?.seasonId || null;
				if (seasonId && !seasonNames.has(seasonId)) {
					const season = await SeasonService.getBySeasonId(seasonId);
					seasonNames.set(seasonId, season?.seasonName || `Season ${seasonId}`);
				}
			} catch (ex) {
				// Fall back to game id when event lookup fails.
			}
			return {
				gameId: game.id,
				eventId: externalEventId,
				label,
				sportCode,
				seasonId,
				seasonName: seasonId ? seasonNames.get(seasonId) : null
			};
		})
	);
	return entries;
};
